/**
 * Mount retrofitting — the bonus-mount grant and its removal.
 *
 * A leaf-handler group of `./leafReducer`'s switch; see `../reducer` for the tier's contract.
 */

import { CommandError, err, ok, type ApplyCtx, type Result } from "@lancer-vtt/shared";
import type { RosterLeafCommand } from "../commands";
import { MOUNT_RETROFITTING_BONUS_ID } from "../composedMounts";
import { slotSizesForMount, weaponFitsSlot } from "../mounts";
import type { LoadoutData, MountData, RosterState, SlotData } from "../types";
import { changed, requireCompendium, requireLoadout, requireMech, requirePilot, withLoadout, withMech, withPilot, type RosterApplyResult } from "./context";

// ── retrofit_mount / un_retrofit_mount ─────────────────────────────────────────

/**
 * Mount Retrofitting (`cb_mount_retrofitting`, RAW: "Replace one mount with a
 * Main/Aux mount"). Hard-gated on the pilot holding the bonus by a literal id
 * check (`MOUNT_RETROFITTING_BONUS_ID`) — unlike `add_mount`/`range`, this bonus
 * carries no structural `bonuses` row to generalize over (it is a player CHOICE).
 * `mountIdx` must address a NATIVE mount
 * (`< loadout.mounts.length` — a bonus-mount tail index naturally fails this and
 * is rejected the same as an out-of-range one); RAW allows exactly ONE retrofit
 * per mech, so a second `retrofit_mount` while `loadout.retrofit` is already set
 * is a hard reject (undo first to re-choose).
 *
 * The type-swap itself is NOT stored here — it's a composed override
 * (`composedMounts.ts`'s `applyRetrofitInPlace`, D3): this command only records
 * WHICH native mount was chosen (`loadout.retrofit`). What IS a real, permanent
 * storage mutation (survives an untake of the bonus; only `undo` reverses it):
 * the mount's `slots` are reshaped to the Main/Aux fixed 2-slot layout right now,
 * and any weapon that no longer fits its new corresponding slot
 * (`weaponFitsSlot` against `["Main","Auxiliary"]`) is dropped (`weaponId`/`modId`
 * cleared). The inverse (`un_retrofit_mount`) captures the WHOLE pre-retrofit
 * mount for a lossless undo — piecewise recapture would be lossy, so this follows
 * the `restore_loadout`/`restore_mech` whole-struct-capture precedent instead of
 * `unequip_weapon`'s per-slot one.
 */
export function applyRetrofitMount(
  state: RosterState,
  cmd: Extract<RosterLeafCommand, { type: "retrofit_mount" }>,
  ctx: ApplyCtx,
): Result<RosterApplyResult, CommandError> {
  const pilotR = requirePilot(state, cmd.pilotId);
  if (!pilotR.ok) return pilotR;
  const pilot = pilotR.value;

  if (!pilot.coreBonuses.includes(MOUNT_RETROFITTING_BONUS_ID)) {
    return err(
      new CommandError(
        `retrofit_mount: pilot ${cmd.pilotId} does not hold ${MOUNT_RETROFITTING_BONUS_ID}`,
      ),
    );
  }

  const mechR = requireMech(pilot, cmd.mechId);
  if (!mechR.ok) return mechR;
  const mech = mechR.value;
  const loadoutR = requireLoadout(mech, cmd.loadoutId);
  if (!loadoutR.ok) return loadoutR;
  const loadout = loadoutR.value;

  // `mountIdx` must be a NATIVE mount — a bonus-mount tail index (>= this length)
  // is rejected the same way an out-of-range index is. Retrofit targets frame-
  // native mounts only; a bonus mount has no "original type" to revert to.
  const originalMount = loadout.mounts[cmd.mountIdx];
  if (!originalMount) {
    return err(
      new CommandError(
        `retrofit_mount: mountIdx ${cmd.mountIdx} is not a native mount (retrofit targets frame-native mounts only, not a bonus mount)`,
      ),
    );
  }
  if (loadout.retrofit) {
    return err(
      new CommandError(
        `retrofit_mount: loadout ${cmd.loadoutId} already has a retrofit at mount ${loadout.retrofit.mountIdx} — undo it first (RAW allows exactly one)`,
      ),
    );
  }

  const compendiumR = requireCompendium(ctx);
  if (!compendiumR.ok) return compendiumR;
  const compendium = compendiumR.value;

  // Reshape to the Main/Aux fixed 2-slot layout, dropping any weapon that no
  // longer fits its new corresponding slot. A stored slot beyond the original
  // mount's length simply doesn't exist yet (nothing to drop); one within it that
  // no longer fits is cleared (weaponId + modId both — a mod dies with its host,
  // same discipline as `unequip_weapon`).
  const targetSlotSizes = slotSizesForMount("Main/Aux") ?? [];
  const nextSlots: SlotData[] = targetSlotSizes.map((fittingSize, i) => {
    const slot = originalMount.slots[i];
    if (!slot || slot.weaponId === null) return slot ?? { weaponId: null };
    const weapon = compendium.getById("weapon", slot.weaponId);
    const weaponSize = weapon ? (weapon as { mount?: string }).mount : undefined;
    if (weaponFitsSlot(fittingSize, weaponSize)) return slot;
    return { weaponId: null, modId: null };
  });

  const nextMount: MountData = { ...originalMount, slots: nextSlots };
  const nextLoadout: LoadoutData = {
    ...loadout,
    mounts: loadout.mounts.map((m, i) => (i === cmd.mountIdx ? nextMount : m)),
    retrofit: { mountIdx: cmd.mountIdx, originalType: originalMount.type },
  };
  const nextMech = withLoadout(mech, nextLoadout);
  const nextPilot = withMech(pilot, nextMech);

  return ok({
    state: withPilot(state, nextPilot),
    inverse: {
      type: "un_retrofit_mount",
      actorId: cmd.actorId,
      pilotId: cmd.pilotId,
      mechId: cmd.mechId,
      loadoutId: cmd.loadoutId,
      mountIdx: cmd.mountIdx,
      originalMount,
    },
    events: changed(cmd.pilotId),
  });
}

/**
 * Internal inverse of `retrofit_mount`: restore the captured pre-retrofit
 * `originalMount` (type + slots, weapon(s)/mod(s) included) verbatim over
 * `loadout.mounts[mountIdx]`, and clear `loadout.retrofit`. Its own inverse is the
 * forward `retrofit_mount` (mirrors `restore_mech`'s pattern) — re-running it
 * against the now-restored state reproduces the same drop deterministically,
 * since the drop is a pure function of the stored mount + compendium weapon sizes.
 */
export function applyUnRetrofitMount(
  state: RosterState,
  cmd: Extract<RosterLeafCommand, { type: "un_retrofit_mount" }>,
): Result<RosterApplyResult, CommandError> {
  const pilotR = requirePilot(state, cmd.pilotId);
  if (!pilotR.ok) return pilotR;
  const pilot = pilotR.value;
  const mechR = requireMech(pilot, cmd.mechId);
  if (!mechR.ok) return mechR;
  const mech = mechR.value;
  const loadoutR = requireLoadout(mech, cmd.loadoutId);
  if (!loadoutR.ok) return loadoutR;
  const loadout = loadoutR.value;

  if (!loadout.retrofit || loadout.retrofit.mountIdx !== cmd.mountIdx) {
    return err(
      new CommandError(
        `un_retrofit_mount: loadout ${cmd.loadoutId} has no retrofit at mount ${cmd.mountIdx} to undo`,
      ),
    );
  }

  const { retrofit: _retrofit, ...restLoadout } = loadout;
  const nextLoadout: LoadoutData = {
    ...restLoadout,
    mounts: loadout.mounts.map((m, i) => (i === cmd.mountIdx ? cmd.originalMount : m)),
  };
  const nextMech = withLoadout(mech, nextLoadout);
  const nextPilot = withMech(pilot, nextMech);

  return ok({
    state: withPilot(state, nextPilot),
    inverse: {
      type: "retrofit_mount",
      actorId: cmd.actorId,
      pilotId: cmd.pilotId,
      mechId: cmd.mechId,
      loadoutId: cmd.loadoutId,
      mountIdx: cmd.mountIdx,
    },
    events: changed(cmd.pilotId),
  });
}
